import { Button, Modal, Tooltip } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { Link as ReactLink, useHistory } from 'react-router-dom';

import { useInvalidateInvoiceReviewListQuery } from '@finance-review/apis/invoice';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useNotifications } from 'modules/app/notifications';
import { useInvalidateGetPayableQueryCache } from 'modules/bookkeep/apis/prepare-payable/useGetPayableQuery/useGetPayableQuery';
import { useSendCardInvoiceBackToReviewMutation } from 'modules/single-use-cards/hooks/useSendCardInvoiceBackToReview';
import {
  useInvalidateSingleUseCardQuery,
  useInvalidateSingleUseCardsListQuery,
} from 'modules/single-use-cards/hooks/useSingleUseCardQuery';
import { routeFor, routes } from 'src/core/constants/routes';

export const SendInvoiceCardBackToReview = ({
  requestId,
  payableId,
  hasPayable,
}: {
  requestId: string;
  payableId?: string;
  hasPayable?: boolean;
}) => {
  const { t } = useTranslation('global');
  const companyId = useCompanyId();
  const history = useHistory();
  const { successNotif, dangerNotif } = useNotifications();

  const [isSendBackToReviewModalOpen, setIsSendBackToReviewModalOpen] =
    useState(false);

  const { mutateAsync: sendInvoiceBackToReview } =
    useSendCardInvoiceBackToReviewMutation();
  const invalidateInvoiceListQuery = useInvalidateInvoiceReviewListQuery();
  const invalidateSingleUseCard = useInvalidateSingleUseCardQuery();
  const invalidateSingleUseCardsListQuery =
    useInvalidateSingleUseCardsListQuery();
  const invalidatePayableListQueryCache = useInvalidateGetPayableQueryCache();

  const sendBackToReview = async () => {
    try {
      const _requestId = requestId;
      await sendInvoiceBackToReview({ requestId: _requestId });
      await invalidateSingleUseCard(_requestId);
      await invalidateSingleUseCardsListQuery();
      await invalidateInvoiceListQuery();
      if (payableId) {
        await invalidatePayableListQueryCache(companyId, payableId);
      }

      // successNotif(t('singleUseCards.actions.sendBackToReviewSuccess'));

      successNotif(
        <Trans
          i18nKey="singleUseCards.actions.sendBackToReviewSuccess"
          values={{
            invoiceLink: t('requests.types.invoice'),
          }}
          components={{
            invoiceLink: (
              <ReactLink
                key="invoiceLink"
                to={routeFor(routes.INVOICES_REVIEW.path, {
                  id: _requestId,
                  company: companyId,
                })}
              >
                -
              </ReactLink>
            ),
          }}
        />,
      );
      history.push(
        routeFor(routes.INVOICES_REVIEW.path, {
          id: requestId,
          company: companyId,
        }),
      );
    } catch {
      dangerNotif(t('singleUseCards.actions.sendBackToReviewError'));
    }
  };

  return (
    <>
      <Tooltip
        content={t('cards.actions.sendInvoiceBackToReviewNoPayable')}
        isDisabled={hasPayable !== false}
      >
        <Button
          fit="parent"
          text={t('invoices.sendBackToReview.buttonText')}
          variant="secondaryNeutral"
          onClick={async () => {
            setIsSendBackToReviewModalOpen(true);
          }}
          isDisabled={hasPayable === false}
        />
      </Tooltip>

      {isSendBackToReviewModalOpen && (
        <Modal
          isOpen
          title={t('invoices.sendBackToReview.alert.title')}
          onClose={() => {
            setIsSendBackToReviewModalOpen(false);
          }}
          iconName="triangle-warning"
          iconVariant="warning"
          subtitle={t('cards.actions.sendInvoiceBackToReviewSubtitle')}
          actions={[
            <Button
              variant="secondaryNeutral"
              text={t('invoices.sendBackToReview.alert.cancel')}
              onClick={() => {
                setIsSendBackToReviewModalOpen(false);
              }}
              key="cancel"
            />,
            <Button
              variant="primaryWarning"
              text={t('invoices.sendBackToReview.alert.confirm')}
              onClick={async () => {
                await sendBackToReview();
                setIsSendBackToReviewModalOpen(false);
              }}
              key="confirm"
            />,
          ]}
        />
      )}
    </>
  );
};
